/**
 * Custo dos Envios do Mercado Livre.
 *
 * A API não entrega este número: testei todas as formas do endpoint de cálculo
 * e todas respondem 404 com token de aplicação. Faz sentido — o custo depende
 * da CONTA (reputação, Full ou Flex, tabela negociada), e mora no painel do
 * vendedor. Então a tabela entra como DADO INFORMADO, com procedência e data,
 * e não como fato que o sistema descobriu sozinho.
 *
 * Três regras que a tabela sozinha não conta, e que mudam o resultado:
 *
 * 1. **O peso que vale é o CONSIDERADO**, o maior entre o real e o cubado. Um
 *    produto leve e volumoso paga pelo volume — e é justamente o caso em que
 *    olhar só a balança subestima o custo.
 * 2. **Abaixo de R$ 19 o custo é limitado à metade do preço.** Sem isso, um
 *    item de R$ 10 apareceria pagando R$ 5,65 de frete quando paga R$ 5,00.
 * 3. **A reputação multiplica.** A mesma venda custa o dobro para quem está em
 *    laranja ou vermelha.
 */

/** A cor que o Mercado Livre atribui à conta. Muda quanto se paga. */
export type Reputacao = 'VERDE' | 'AMARELA' | 'LARANJA_VERMELHA';

export const ROTULO_REPUTACAO: Record<Reputacao, string> = {
  VERDE: 'Verde, MercadoLíder ou sem reputação',
  AMARELA: 'Amarela',
  LARANJA_VERMELHA: 'Laranja ou vermelha',
};

/**
 * Traduz o nível de reputação do Mercado Livre para a faixa que a tabela usa.
 *
 * A API devolve `level_id` como `5_green`, `3_yellow`, `1_red`. Só a COR
 * importa para o custo — os números são degraus dentro da cor.
 *
 * `null` quando o nível é desconhecido ou ausente: conta nova ainda sem
 * reputação formada entra como verde na tabela oficial, mas isso é decisão de
 * quem opera, não suposição minha. Devolver null faz a tela perguntar.
 */
export function reputacaoDoNivelML(levelId: unknown): Reputacao | null {
  if (typeof levelId !== 'string') return null;
  const cor = levelId.split('_').slice(1).join('_').toLowerCase();
  if (cor === 'green' || cor === 'light_green') return 'VERDE';
  if (cor === 'yellow') return 'AMARELA';
  if (cor === 'orange' || cor === 'red') return 'LARANJA_VERMELHA';
  return null;
}

/**
 * A tabela do Custo dos Envios.
 *
 * `valoresCents[faixaPeso][faixaPreco]` guarda a tabela VERDE — as outras
 * reputações saem dela por multiplicador. Guardar as três seria guardar a mesma
 * informação três vezes, e deixar duas delas envelhecerem em silêncio.
 */
export interface TabelaFrete {
  /** Limite SUPERIOR de cada faixa de peso, em gramas. O último é `null` (acima de tudo). */
  faixasPesoGramas: ReadonlyArray<number | null>;
  /** Limite SUPERIOR de cada faixa de preço, em centavos. O último é `null`. */
  faixasPrecoCents: ReadonlyArray<number | null>;
  /** Valores da tabela verde, em centavos. */
  valoresCents: ReadonlyArray<ReadonlyArray<number>>;
  /** De onde veio e quando — sem isso ninguém sabe se ainda vale. */
  fonte: string;
  vigenteDesde: string;
  conferidaEm: string;
}

/**
 * Peso cubado, em gramas.
 *
 * Fórmula do Mercado Livre: (comprimento × largura × altura em cm) ÷ 6.000, em
 * quilos. Como as medidas do produto ficam em MILÍMETROS, a conversão de
 * unidade cancela e sobra dividir por 6.000 — o que dá o resultado direto em
 * gramas.
 */
export function pesoCubadoGramas(
  comprimentoMm: number | null,
  larguraMm: number | null,
  alturaMm: number | null,
): number | null {
  if (!comprimentoMm || !larguraMm || !alturaMm) return null;
  if (comprimentoMm <= 0 || larguraMm <= 0 || alturaMm <= 0) return null;
  return Math.round((comprimentoMm * larguraMm * alturaMm) / 6_000);
}

/**
 * O peso que a tabela usa: o MAIOR entre o real e o cubado.
 *
 * Devolve `null` quando não dá para saber nenhum dos dois — e `null` leva a
 * análise a dizer que falta dado, nunca a assumir o mais leve.
 */
export function pesoConsideradoGramas(
  pesoRealGramas: number | null,
  cubadoGramas: number | null,
): number | null {
  if (pesoRealGramas === null && cubadoGramas === null) return null;
  return Math.max(pesoRealGramas ?? 0, cubadoGramas ?? 0);
}

/** Índice da faixa em que o valor cai. Faixa é "até X", com o último aberto. */
function faixaDe(valor: number, limites: ReadonlyArray<number | null>): number {
  for (let i = 0; i < limites.length; i += 1) {
    const teto = limites[i];
    if (teto === null || teto === undefined) return i;
    if (valor <= teto) return i;
  }
  return limites.length - 1;
}

export type MotivoSemFrete =
  /** Sem peso e sem dimensões: não dá para achar a faixa. */
  | 'SEM_PESO'
  /** A tabela não cobre esta combinação — formato inesperado. */
  | 'TABELA_INCOMPLETA'
  /**
   * A fonte não informa o multiplicador desta reputação nesta faixa de preço.
   * Ela só documenta laranja/vermelha a partir de R$ 79.
   */
  | 'MULTIPLICADOR_DESCONHECIDO';

export interface FreteCalculado {
  definido: true;
  custoCents: number;
  /** O peso que a tabela usou, para a tela poder explicar de onde veio. */
  pesoConsideradoGramas: number;
  /** true quando o volume mandou, não a balança. */
  cubadoMandou: boolean;
  /** true quando o teto de metade do preço (abaixo de R$ 19) foi aplicado. */
  limitadoPelaMetade: boolean;
  /** O valor cheio da tabela verde, antes da reputação e do teto. */
  valorDeTabelaCents: number;
}

export type ResultadoFrete = FreteCalculado | { definido: false; motivo: MotivoSemFrete };

/** Abaixo deste preço, o custo é limitado à metade do valor do produto. */
export const PRECO_LIMITE_METADE_CENTS = 1_900;

/** A partir deste preço o frete grátis é obrigatório, e o multiplicador muda. */
export const PRECO_FRETE_GRATIS_CENTS = 7_900;

/**
 * Multiplicador da reputação sobre a tabela verde.
 *
 * `null` significa que a fonte NÃO informa esse caso — e não que seja 1. A
 * documentação do Custo dos Envios descreve laranja/vermelha apenas a partir de
 * R$ 79; abaixo disso ela é silenciosa, e inventar 1,0 ali produziria um custo
 * que parece conferido.
 */
export function multiplicadorReputacao(
  reputacao: Reputacao,
  precoCents: number,
): number | null {
  const acimaDoFreteGratis = precoCents >= PRECO_FRETE_GRATIS_CENTS;
  switch (reputacao) {
    case 'VERDE':
      return 1;
    case 'AMARELA':
      return acimaDoFreteGratis ? 1.2 : 1.15;
    case 'LARANJA_VERMELHA':
      return acimaDoFreteGratis ? 2 : null;
  }
}

export function custoEnvio(
  tabela: TabelaFrete,
  entrada: {
    pesoRealGramas: number | null;
    comprimentoMm: number | null;
    larguraMm: number | null;
    alturaMm: number | null;
    precoCents: number;
    reputacao: Reputacao;
  },
): ResultadoFrete {
  const cubado = pesoCubadoGramas(entrada.comprimentoMm, entrada.larguraMm, entrada.alturaMm);
  const peso = pesoConsideradoGramas(entrada.pesoRealGramas, cubado);
  if (peso === null || peso <= 0) return { definido: false, motivo: 'SEM_PESO' };

  const linha = tabela.valoresCents[faixaDe(peso, tabela.faixasPesoGramas)];
  const valorDeTabelaCents = linha?.[faixaDe(entrada.precoCents, tabela.faixasPrecoCents)];
  if (valorDeTabelaCents === undefined) return { definido: false, motivo: 'TABELA_INCOMPLETA' };

  const fator = multiplicadorReputacao(entrada.reputacao, entrada.precoCents);
  if (fator === null) return { definido: false, motivo: 'MULTIPLICADOR_DESCONHECIDO' };

  const comReputacao = Math.round(valorDeTabelaCents * fator);

  // Abaixo de R$ 19 o Custo dos Envios não passa da metade do preço.
  const teto = Math.floor(entrada.precoCents / 2);
  const limitadoPelaMetade =
    entrada.precoCents < PRECO_LIMITE_METADE_CENTS && comReputacao > teto;

  return {
    definido: true,
    custoCents: limitadoPelaMetade ? teto : comReputacao,
    pesoConsideradoGramas: peso,
    cubadoMandou: cubado !== null && cubado > (entrada.pesoRealGramas ?? 0),
    limitadoPelaMetade,
    valorDeTabelaCents,
  };
}

export const RECADO_SEM_FRETE: Record<MotivoSemFrete, string> = {
  SEM_PESO:
    'Sem peso nem dimensões não dá para achar a faixa do frete. Preencha na ficha da mercadoria — a tabela cobra pelo maior entre o peso real e o cubado.',
  TABELA_INCOMPLETA: 'A tabela de frete cadastrada não cobre esta faixa de peso ou de preço.',
  MULTIPLICADOR_DESCONHECIDO:
    'A fonte da tabela só informa o acréscimo de reputação laranja/vermelha a partir de R$ 79. Abaixo disso o valor não está documentado — confirme no painel da sua conta.',
};
